import React, { useCallback, useEffect, useState } from "react";
import {
	Button,
	Elem,
	ElemBefore,
	IconButton,
	SearchInput,
	SearchSelect,
	SearchSelectOptionProps,
	Select,
} from "@alm/flow-ds/components";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import PageHeader from "components/PageHeader";
import api from "../../../lib/api";
import ActivityInstancesDropdown from "./ActivityInstancesDropdown";
import ActivityInstancesDates from "./ActivityInstancesDates";
import { DateOperators } from "../../../lib/api/services/workflow";
import { useDispatch } from "react-redux";
import { createAlertAction } from "data/app/appActions";
import { createAlert } from "records/Alert";

interface Props {
	onChangeSearchValue: (value: string) => void;
	onChangeCreatedByValue: React.Dispatch<
		React.SetStateAction<SearchSelectOptionProps | null>
	>;
	onChangeCreatedValue: (value: string) => void;
	onChangeOperatorValue: (value: DateOperators) => void;
	onChangeStatusValue: (value: string) => void;
	createdBy: SearchSelectOptionProps | null;
	created: string;
	operator: DateOperators;
	status: string;
	search: string;
}

const ActivityInstancesHeader = ({
	onChangeSearchValue,
	onChangeStatusValue,
	onChangeCreatedByValue,
	onChangeCreatedValue,
	onChangeOperatorValue,
	createdBy,
	created,
	operator,
	status,
	search,
}: Props) => {
	const reduxDispatch = useDispatch();
	// State for member options
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [memberOptions, setMemberOptions] = useState<
		SearchSelectOptionProps[] | undefined
	>(undefined);
	const { t } = useTranslation();

	const history = useHistory();
	const location = useLocation();

	const [mobileOpen, setMobileOpen] = useState(false);

	const [searchBox, setSearchBox] = useState(search);

	const [isDatesOpen, setIsDatesOpen] = useState<boolean>(false);
	const [datesActive, setDatesActive] = useState<number | null>(null);

	const DESKTOP_WIDTH = 1130;

	const [width, setWidth] = useState(window.innerWidth);
	const handleWindowSizeChange = () => setWidth(window.innerWidth);
	useEffect(() => {
		window.addEventListener("resize", handleWindowSizeChange);
		return () => {
			window.removeEventListener("resize", handleWindowSizeChange);
		};
	}, []);

	// Get all users to use as options
	const getMemberOptions = useCallback(async () => {
		setOptionsLoading(true);
		try {
			const res = await api.roles.getAllUsers();
			const options = res.map((user: string) => {
				return { label: user, value: user };
			});
			setMemberOptions(options);
			setOptionsLoading(false);
		} catch (e) {
			console.error(e);
			reduxDispatch(
				createAlertAction(
					createAlert({
						title: t("alerts.getUsersListError.title"),
						description: t("alerts.getUsersListError.description"),
						status: "error",
						timeout: 3000,
					})
				)
			);
		}
	}, [reduxDispatch, t]);

	useEffect(() => {
		getMemberOptions();
	}, [getMemberOptions]);

	const handleSearchChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			addSentryBreadcrumb({
				category: "input.change",
				message: `Search input valued changed to "${value}"`,
			});

			if (value !== "") {
				setSearchBox(value);
			} else {
				setSearchBox(value);
				onChangeSearchValue(value);
				const params = new URLSearchParams(location.search);
				params.delete("search");

				history.push({
					pathname: location.pathname,
					search: params.toString(),
				});
			}
		},
		[history, location.pathname, location.search, onChangeSearchValue]
	);

	const handleSelectChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.currentTarget.value;
			onChangeStatusValue(value);

			addSentryBreadcrumb({
				category: "input.change",
				message: `Status Select value changed to "${value}"`,
			});

			const params = new URLSearchParams(location.search);
			params.delete("status");
			if (value !== "all") {
				params.set("status", value);
			}

			history.push({
				pathname: location.pathname,
				search: params.toString(),
			});
		},
		[history, location.pathname, location.search, onChangeStatusValue]
	);

	const handleCreatedByChange = useCallback(
		newOption => {
			const newVal = newOption !== null ? newOption.value : "";
			onChangeCreatedByValue(newOption);

			addSentryBreadcrumb({
				category: "input.change",
				message: `Created by select value changed to "${newVal}"`,
			});

			const params = new URLSearchParams(location.search);
			params.delete("createdBy");
			if (newVal !== "") {
				params.set("createdBy", newVal);
			}

			history.push({
				pathname: location.pathname,
				search: params.toString(),
			});
		},
		[history, location.pathname, location.search, onChangeCreatedByValue]
	);

	const handleCreatedChange = useCallback(
		(createdValue: string, operatorValue: DateOperators) => {
			if (operatorValue !== "ALL") onChangeCreatedValue(createdValue);
			else onChangeCreatedValue("");
			onChangeOperatorValue(operatorValue);

			addSentryBreadcrumb({
				category: "input.change",
				message: `Created date value changed to "${createdValue}"`,
			});

			const params = new URLSearchParams(location.search);
			params.delete("created");
			params.delete("operator");
			if (createdValue !== "" && operatorValue !== "ALL") {
				params.set("created", createdValue);
				params.set("operator", operatorValue);
			}

			history.push({
				pathname: location.pathname,
				search: params.toString(),
			});
		},

		[
			history,
			location.pathname,
			location.search,
			onChangeCreatedValue,
			onChangeOperatorValue,
		]
	);

	const handleReset = useCallback(() => {
		onChangeCreatedByValue(null);
		onChangeCreatedValue("");
		setSearchBox("");
		onChangeStatusValue("all");
		onChangeOperatorValue("ALL");
		history.push({
			pathname: location.pathname,
		});
	}, [
		history,
		location.pathname,
		onChangeCreatedByValue,
		onChangeCreatedValue,
		onChangeStatusValue,
		onChangeOperatorValue,
	]);

	const handleSearch = useCallback(() => {
		const params = new URLSearchParams(location.search);
		onChangeSearchValue(searchBox);
		params.delete("search");
		if (searchBox !== "") params.set("search", searchBox);

		history.push({
			pathname: location.pathname,
			search: params.toString(),
		});
	}, [
		history,
		location.pathname,
		location.search,
		onChangeSearchValue,
		searchBox,
	]);

	const closeDates = useCallback(() => {
		setIsDatesOpen(false);
		setDatesActive(null);
	}, [setIsDatesOpen, setDatesActive]);

	const openDates = useCallback(() => {
		setIsDatesOpen(true);
	}, [setIsDatesOpen]);

	const toggleDates = useCallback(() => {
		const modalClass = document.querySelector(".ReactModalPortal");
		if (modalClass === null) {
			isDatesOpen ? closeDates() : openDates();
		}
	}, [closeDates, isDatesOpen, openDates]);

	useEffect(() => {
		if (search !== searchBox) setSearchBox(search);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	const getSearchBarAndStatus = (
		<>
			<div className="search-bar search-bar--desktop search-bar--instances">
				<SearchInput
					label={t("main.text.search")}
					onSearch={handleSearch}
					onChange={handleSearchChange}
					value={searchBox}
					className="mb-0 "
				/>
			</div>
			<Select
				className="activity-instances--status"
				value={status}
				onChange={handleSelectChange}
				label={t("main.activityInstances.headings.status")}>
				<>
					<option value="all">
						{t("main.activityInstances.headings.allInstances")}
					</option>
					<option value="active">
						{t("main.activityInstances.headings.active")}
					</option>
					<option value="completed">
						{t("main.activityInstances.headings.completed")}
					</option>
				</>
			</Select>
		</>
	);

	const getCreatedCreatorAndReset = (
		<>
			<Elem
				aria-label={t("main.activityInstances.headings.created")}
				className="elem-date-dropdown">
				<ElemBefore
					label={t("main.activityInstances.headings.created")}
				/>
				<ActivityInstancesDropdown
					className="activity-instances-dateDropdown"
					id={`activityInstanceDates`}
					menuBtn={
						<Button
							baseCssClasses="form-select"
							label={
								operator === "ALL"
									? t("main.activityInstances.text.allDates")
									: operator === "AFTER"
									? t("main.activityInstances.text.after", {
											date: created,
									  })
									: operator === "BEFORE"
									? t("main.activityInstances.text.before", {
											date: created,
									  })
									: operator === "BETWEEN"
									? t("main.activityInstances.text.between", {
											date: created.substring(
												0,
												created.indexOf(",")
											),
											date2: created.substring(
												created.indexOf(",") + 1
											),
									  })
									: created
							}
						/>
					}
					align="left"
					open={openDates}
					close={closeDates}
					toggle={toggleDates}
					active={datesActive}
					isOpen={isDatesOpen}>
					<div className="dropdown-item">
						<ActivityInstancesDates
							id="activity-instances-dates"
							options={[
								{ value: "ALL", label: "All dates" },
								{ value: "BETWEEN", label: "Between" },
								{ value: "BEFORE", label: "Before" },
								{ value: "AFTER", label: "After" },
								{ value: "EXACT", label: "Exact" },
							]}
							value={created}
							operator={operator}
							label="activity-instances-dates__dropdown"
							labelHidden={true}
							onChange={handleCreatedChange}
							close={closeDates}
							setActive={setDatesActive}
						/>
					</div>
				</ActivityInstancesDropdown>
			</Elem>

			<SearchSelect
				className="activity-instances-searchSelect"
				clearable={true}
				loading={optionsLoading}
				options={memberOptions}
				onChange={handleCreatedByChange}
				value={createdBy}
				label={t("main.activityInstances.headings.createdBy")}
			/>
			{!(
				createdBy === null &&
				created === "" &&
				searchBox === "" &&
				status === "all" &&
				operator === "ALL"
			) && (
				<Button
					className="btn ui-btn btn-outline-primary activity-instances--reset"
					label={t("main.activityInstances.headings.reset")}
					onClick={handleReset}
				/>
			)}
		</>
	);

	return (
		<div className="tasks__header-container--managers">
			<PageHeader
				title={t("main.activityInstances.headings.activityInstances")}
				className="page-header__activity-instances">
				<div
					className="search-bar search-bar--icon"
					aria-expanded={mobileOpen}>
					<IconButton
						aria-label={t("main.icons.openSearchBox")}
						active={mobileOpen}
						className="btn-basic--primary btn--round"
						icon="search"
						onClick={() => setMobileOpen(!mobileOpen)}
					/>
				</div>
			</PageHeader>
			<div className="nav activity-instances__nav">
				{mobileOpen && (
					<div className="search-bar--mobile">
						<SearchInput
							label={t("main.text.search")}
							onSearch={handleSearch}
							onChange={handleSearchChange}
							value={searchBox}
						/>
					</div>
				)}

				{width <= DESKTOP_WIDTH ? (
					<>
						<div className="tasks__nav-wrapper">
							{getSearchBarAndStatus}
						</div>
						<div className="tasks__nav-wrapper">
							{getCreatedCreatorAndReset}
						</div>
					</>
				) : (
					<>
						{getSearchBarAndStatus}
						{getCreatedCreatorAndReset}
					</>
				)}
			</div>
		</div>
	);
};

export default ActivityInstancesHeader;
